@file:Suppress("FunctionName")

package components

import Toast
import kotlinx.browser.document
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.html.*
import kotlinx.html.dom.append
import mainScope
import net.gorillagroove.api.UserNotificationId
import net.gorillagroove.notification.UserNotification
import net.gorillagroove.notification.UserNotificationService
import net.gorillagroove.util.Formatter
import net.gorillagroove.util.GGLog
import net.gorillagroove.util.GGLog.logError
import onClickSuspend
import org.w3c.dom.HTMLElement
import queryId
import kotlin.time.Duration.Companion.seconds


private val header get() = document.queryId<HTMLElement>("user-notifications-header")

fun TagConsumer<*>.UserNotificationPage() = div("full-height") {
    id = "user-notifications"

    div {
        id = "user-notifications-header"
    }

    div {
        table("page-table") {
            thead {
                tr {
                    th {
                        style = "width:40px"
                        span { + "New" }
                    }
                    th {
                        span { +"Message" }
                    }
                    th {
                        span { +"Sent" }
                    }

                    th(classes = "text-center") {
                        style = "width:40px"
                        span {
                            style = "width:40px"

                            button(classes = "table") {
                                i("fa-solid fa-trash") {
                                    onClickSuspend = {
                                        UserNotifications.deleteAll()
                                    }
                                }
                            }
                        }
                    }
                }
            }

            tbody {
                id = "user-notification-table-body"
            }
        }
    }

    mainScope.launch {
        header.append(
            HeaderButton(iconClasses = "fa-solid fa-pen-to-square", text = "Notification Settings") {
                Dialog.show(UserNotificationSettings())
            }
        )
        UserNotifications.getNotifications(refreshUnreads = true)
    }
}

private object UserNotifications {
    private var unreadIds = emptySet<UserNotificationId>()
    private var notifications = emptyList<UserNotification>()

    init {
        UserNotificationService.eventRegistration.register {
            if (isLoaded()) {
                getNotifications(refreshUnreads = false)
            }
        }
    }

    private fun isLoaded(): Boolean = document.getElementById("user-notifications") != null

    fun getNotifications(refreshUnreads: Boolean) {
        try {
            notifications = UserNotificationService.getNotifications()

            val unreads = notifications.filterNot { it.read }.map { it.id }.toSet()

            // Unread IDs are stored separately, because if notifications reload automatically, we don't want the icons
            // to just disappear. They should only disappear if we navigate away and back. However, we always want to
            // add the latest unreads to the set so we capture new things that come in while on the page.
            unreadIds = if (refreshUnreads) {
                unreads
            } else {
                unreadIds + unreads
            }

            if (unreads.isNotEmpty()) {
                markRead()
            }
        } catch (e: Exception) {
            Toast.error("Failed to load notifications")
            GGLog.logError("Failed to load notifications!", e)

            return
        }

        val body = document.queryId<HTMLElement>("user-notification-table-body")
        body.innerHTML = ""

        if (notifications.isEmpty()) {
            body.append {
                tr {
                    td(classes = "text-center") {
                        colSpan = "4"
                        + "You have no notifications. This is probably a good thing?"
                    }
                }
            }
        }

        notifications.forEach { notification ->
            body.append {
                tr {
                    td("text-center") {
                        if (unreadIds.contains(notification.id)) {
                            i("fa-solid fa-circle danger-color")
                        }
                    }
                    td { + notification.message }
                    td {
                        span {
                            tooltip = Formatter.formatToUserTimeFormat(notification.createdAt)
                            tooltipDelay = 250

                            + notification.createdAgoString
                        }
                    }
                    td(classes = "text-center") {
                        span {
                            button(classes = "icon action-button") {
                                onClickSuspend = { UserNotificationService.delete(listOf(notification.id)) }

                                i("fa-solid fa-xmark")
                            }
                        }
                    }
                }
            }
        }

        Tooltip.registerAll(body)
    }

    private fun markRead() {
        mainScope.launch {
            // Wait a second so if somebody clicks into this page hella fast on accident it doesn't get marked right away
            delay(1.seconds)

            if (isLoaded()) {
                UserNotificationService.markRead(unreadIds.toList())
            }
        }
    }

    fun deleteAll() {
        val ids = notifications.map { it.id }
        UserNotificationService.delete(ids)
    }
}
